import { FUTURES } from "../../constants";
import config from "../../config";
import {
  applyDiffUpdate,
  isValidDiffEvent,
  normalizeSnapshot,
  shouldSkipBySequence,
} from "../../helpers/futures/orderBookEngine";

const initState = {
  pair: config.default_futures_pair,
  price: null,
  chartPrice: null,
  chartDirection: null,
  tickersLoading: false,
  orderBookLoading: false,
  markPrice: null,
  // Binance event time (ms) of the mark price tick shown on the page; sent with a new position
  markPriceTickAt: null,
  openPositions: [],
  // The position just opened from this page, shown in the list before the next poll
  openedPosition: null,
  positionMarkPrices: {},
  tickerData: null,
  tickersMap: {},
  orderBook: null,
  orderBookSync: {
    isSynced: false,
    lastUpdateId: 0,
    lastAppliedSeq: 0,
  },
  notifications: [],
  positionLevelsVisible: true,
  orders: {},
  // Close asked outside the positions table (chart line), handled by Positions
  positionCloseRequest: null,
};

const tradingReducer = (state = initState, action) => {
  switch (action.type) {
    // Same pair, fresher object from the backend list (e.g. adds `id`): keep prices and order book
    case FUTURES.SYNC_PAIR:
      return {
        ...state,
        pair: action.payload,
      };

    case FUTURES.CHANGE_PAIR:
      return {
        ...state,
        pair: action.payload,
        price: null,
        markPriceTickAt: null,
        chartPrice: null,
        chartDirection: null,
        orderBookSync: {
          isSynced: false,
          lastUpdateId: 0,
          lastAppliedSeq: 0,
        },
      };

    case FUTURES.UPDATE_PRICE:
      return {
        ...state,
        price: action.payload,
      };

    case FUTURES.UPDATE_CHART_PRICE:
      if (
        action?.meta?.pair &&
        String(action.meta.pair).toLowerCase() !==
          String(state.pair?.slug || "").toLowerCase()
      ) {
        return state;
      }

      return {
        ...state,
        chartPrice: action.payload?.price ?? null,
        chartDirection: action.payload?.direction ?? null,
      };

    case FUTURES.CHANGE_MARK_PRICE:
      return {
        ...state,
        markPrice: action.payload,
      };

    case FUTURES.SET_MARK_TICK_AT:
      return {
        ...state,
        markPriceTickAt: action.payload,
      };

    case FUTURES.POSITION_OPENED:
      return {
        ...state,
        openedPosition: action.payload,
      };

    case FUTURES.SET_OPEN_POSITIONS:
      return {
        ...state,
        openPositions: action.payload,
      };

    case FUTURES.SET_POSITION_MARK_PRICES:
      return {
        ...state,
        positionMarkPrices: action.payload,
      };

    case FUTURES.CHANGE_TICKER_DATA:
      return {
        ...state,
        tickerData: action.payload,
      };

    case FUTURES.UPDATE_ORDER_BOOK:
      // Sources:
      // - REST snapshot (`/depth`) → full replace, set synced baseline
      // - WS diff updates → sequence-guarded incremental merge
      // Pair guard: ignore updates from stale socket/snapshot.
      if (
        action?.meta?.pair &&
        String(action.meta.pair) !==
          String(state.pair?.slug || "").toLowerCase()
      ) {
        return state;
      }

      // A partial-depth stream message is a full top-of-book snapshot too
      if (["rest", "stream"].includes(action?.meta?.source)) {
        const normalized = normalizeSnapshot(action.payload);

        return {
          ...state,
          orderBook: {
            ...(action.payload || {}),
            asks: normalized.asks,
            bids: normalized.bids,
            lastUpdateId: normalized.lastUpdateId,
          },
          orderBookSync: {
            ...state.orderBookSync,
            isSynced: true,
            lastUpdateId: normalized.lastUpdateId,
            lastAppliedSeq: normalized.lastUpdateId,
          },
        };
      }

      if (action?.meta?.source === "ws") {
        const prevSync = state.orderBookSync || {
          isSynced: false,
          lastUpdateId: 0,
          lastAppliedSeq: 0,
        };

        // If UI isn't synced yet, we ignore here;
        // buffering/replay is handled in the socket thunk.
        if (!prevSync.isSynced) return state;

        const evt = action.payload;

        if (!isValidDiffEvent(evt)) return state;

        if (shouldSkipBySequence(evt, prevSync.lastAppliedSeq)) {
          return state;
        }

        const nextBook = applyDiffUpdate(state.orderBook, evt);
        const nextSeq =
          Number(evt.u) || prevSync.lastAppliedSeq;

        return {
          ...state,
          orderBook: nextBook,
          orderBookSync: {
            ...prevSync,
            lastUpdateId:
              nextBook.lastUpdateId || nextSeq,
            lastAppliedSeq: nextSeq,
          },
        };
      }

      return state;

    case FUTURES.FUTURE_TICKERS_LOADING:
      return {
        ...state,
        tickersLoading: action.payload,
      };

    case FUTURES.UPDATE_ALL_TICKERS_CACHE:
      return {
        ...state,
        tickersMap: action.payload,
      };

    case FUTURES.FUTURE_ORDER_BOOK_LOADING:
      return {
        ...state,
        orderBookLoading: action.payload,
      };

    case FUTURES.SHOW_NOTIFICATION:
      console.log(state);

      return {
        ...state,
        notifications: state?.notifications?.length
          ? [...state.notifications, action.payload]
          : [action.payload],
      };

    case FUTURES.HIDE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          (el) => el.id !== action.payload,
        ),
      };

    case FUTURES.REQUEST_POSITION_CLOSE:
      return {
        ...state,
        positionCloseRequest: { positionId: action.payload },
      };

    case FUTURES.CLEAR_POSITION_CLOSE_REQUEST:
      return {
        ...state,
        positionCloseRequest: null,
      };

    case FUTURES.SET_POSITION_LEVELS_VISIBLE:
      return {
        ...state,
        positionLevelsVisible: action.payload,
      };

    default:
      return state;
  }
};

export default tradingReducer;
